// ./src/contexts/ExpenseTagsContext/ExpenseTagsContext.tsx
//
// Thin context wrapper around the useExpenseTags hook.
//
// All derived data (activeTags, totalTagsCount, hasTags, getTagsByIds,
// createTagInline, getNextAutoColor) now lives in the hook itself.
//
// Usage:
//   <ExpenseTagsProvider autoLoad>
//     <MyComponent />          {/* reads via useExpenseTagsContext() */}
//   </ExpenseTagsProvider>
//
// Two accessors are exported:
//   useExpenseTagsContext()         — throws if no provider (for components that *require* it)
//   useOptionalExpenseTagsContext() — returns null (for components that can work with or without)

import React, { createContext, useContext } from "react";

import {
  useExpenseTags,
  UseExpenseTagsOptions,
  UseExpenseTagsReturn,
} from "@/hooks/useExpenseTags";

// ============================================
// Context
// ============================================

const ExpenseTagsContext = createContext<UseExpenseTagsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface ExpenseTagsProviderProps extends UseExpenseTagsOptions {
  children: React.ReactNode;
}

/**
 * Wraps children with a shared ExpenseTags state.
 * Each provider instance creates its own independent hook,
 * so two providers on different pages won't interfere.
 */
export const ExpenseTagsProvider: React.FC<ExpenseTagsProviderProps> = ({
  children,
  ...options
}) => {
  const value = useExpenseTags(options);

  return (
    <ExpenseTagsContext.Provider value={value}>
      {children}
    </ExpenseTagsContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

/**
 * Returns the ExpenseTags context value.
 * Throws if called outside an <ExpenseTagsProvider>.
 */
export const useExpenseTagsContext = (): UseExpenseTagsReturn => {
  const context = useContext(ExpenseTagsContext);

  if (!context) {
    throw new Error(
      "useExpenseTagsContext must be used within an <ExpenseTagsProvider>. " +
      "Wrap your component tree with <ExpenseTagsProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

/**
 * Returns the ExpenseTags context value, or null if no provider is present.
 * Use this in components that should work both inside and outside a provider
 * (e.g. reusable list/drawer components).
 */
export const useOptionalExpenseTagsContext = (): UseExpenseTagsReturn | null => {
  return useContext(ExpenseTagsContext);
};